/**
 * A palette of colors used across the application.
 *
 * primaryYellow: The main color used for primary elements and to indicate interactivity.
 * secondaryPetrol: The color used for secondary elements, providing contrast to the primary color.
 * secondaryPetrolHover: The color used for hover states of secondary elements.
 * disable: The color used to indicate disabled interactive elements.
 * active: The color used to indicate active elements or selections.
 * primaryText: The main color used for text content.
 *
 * These colors are used in the theme creation in order to maintain
 * consistency and to simplify color management across the application.
 */

/**
 * @typedef {object} Color
 * @property {string} main - The main color value.
 * @property {string} [light] - The lighter variant of the color.
 * @property {string} [dark] - The darker variant of the color.
 * @property {string} [lightest] - The lightest variant of the color.
 * @property {string} [darkest] - The darkest variant of the color.
 * @property {string} [contrastText] - The contrast text color for this color.
 */

/**
 * @typedef {Color} ColorWithAlphas
 * @property {string} alpha4 - The color with 4% alpha transparency.
 * @property {string} alpha8 - The color with 8% alpha transparency.
 * @property {string} alpha12 - The color with 12% alpha transparency.
 * @property {string} alpha30 - The color with 30% alpha transparency.
 * @property {string} alpha50 - The color with 50% alpha transparency.
 * @property {string} alpha75 - The color with 75% alpha transparency.
 */

/**
 * Adds alpha transparency variants to a color object.
 *
 * @param {Color} color - The base color object.
 * @returns {ColorWithAlphas} The color object extended with alpha variants.
 */
import { alpha } from '@mui/material/styles';

const withAlphas = (color) => {
  return {
    ...color,
    alpha4: alpha(color.main, 0.04),
    alpha8: alpha(color.main, 0.08),
    alpha12: alpha(color.main, 0.12),
    alpha30: alpha(color.main, 0.3),
    alpha50: alpha(color.main, 0.5),
    alpha75: alpha(color.main, 0.75),
  };
};

export const white = {
  default: '#FFFFFF',
  light: '#F2F2F2',
};

export const neutral = {
  50: '#F8F9FA',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D2D6DB',
  400: '#9DA4AE',
  500: '#6C737F',
  600: '#4D5761',
  700: '#2F3746',
  800: '#1C2536',
  900: '#111927',
};

export const grey = {
  grey5: '#FAFAFA', // grey contrast box
};

export const petrol = withAlphas({
  lightest: '#145A66',
  light: '#114B55',
  main: '#0E3B43',
  dark: '#0A2D33',
  darkest: '#071E22',
  contrastText: '#FFFFFF',
});

export const yellow = withAlphas({
  lightest: '#F7E9B4', // 100
  light: '#FCF9DC', // 300
  main: '#EAC435', // 400
  dark: '#E79B18', // 700
  darkest: '#E46F06', // 900
  contrastText: '#0E3B43',
});

export const textColors = {
  primaryText: '#323232', // paragraphs etc
};

/* STATES */
export const success = withAlphas({
  main: '#2ECC71',
});

export const info = withAlphas({
  main: '#95B8D1', // removed dark, light variants because mui creates them automatically
  // dark: theme.palette.info.dark
  // light: theme.palette.info.light
});

export const warning = withAlphas({
  main: '#FFAA00', // removed dark, light variants because mui creates them automatically
  // dark: theme.palette.warning.dark
  // light: theme.palette.warning.light
});

export const error = withAlphas({
  main: '#F04438',
});
